import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Check, Copy, KeyRound, ShieldCheck, Terminal } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { useHubToast } from "@/components/ui/HubToast";
import { Input, Label } from "@/components/ui/Input";
import { apiFetch } from "@/lib/api";

interface InviteStatusResponse {
  email: string;
  displayName: string;
  linuxUsername: string;
  hasPublicKey: boolean;
  sshKeyFingerprint: string | null;
  inviteExpiresAt: string | null;
  hosts: Array<{
    host_key: string;
    display_name: string;
    hostname_or_ip: string;
    ssh_port: number;
    jump_host_key: string | null;
    access_tier: string;
    expires_at: string | null;
  }>;
  instructions: string;
}

export function ServerAccessInvitePage() {
  const { token = "" } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();

  const [publicKeyInput, setPublicKeyInput] = useState("");
  const [copied, setCopied] = useState(false);

  const inviteQuery = useQuery({
    queryKey: ["public-server-access-invite", token],
    queryFn: () =>
      apiFetch<InviteStatusResponse>(`/api/public/server-access-invite/${encodeURIComponent(token)}`),
    enabled: Boolean(token),
    retry: false,
  });

  const submitKeyMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; sshKeyFingerprint: string }>(
        `/api/public/server-access-invite/${encodeURIComponent(token)}/ssh-key`,
        {
          method: "POST",
          body: JSON.stringify({ sshPublicKey: publicKeyInput.trim() }),
        },
      ),
    onSuccess: (res) => {
      setPublicKeyInput("");
      void queryClient.invalidateQueries({ queryKey: ["public-server-access-invite", token] });
      showToast({
        message: `SSH public key registered (${res.sshKeyFingerprint}). Target servers sync within 60 seconds.`,
        tone: "success",
      });
    },
    onError: (error) => {
      showToast({
        message: error instanceof Error ? error.message : "Could not register SSH public key.",
        tone: "error",
      });
    },
  });

  if (inviteQuery.isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-12">
        <div className="mx-auto max-w-2xl text-sm text-slate-600">Loading server access invitation…</div>
      </div>
    );
  }

  if (inviteQuery.isError || !inviteQuery.data) {
    const message =
      inviteQuery.error instanceof Error
        ? inviteQuery.error.message
        : "This server access invite link is invalid or has expired.";
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-12">
        <div className="mx-auto max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>Server SSH Access Invitation</CardTitle>
            </CardHeader>
            <CardBody className="text-sm text-rose-700">{message}</CardBody>
          </Card>
        </div>
      </div>
    );
  }

  const data = inviteQuery.data;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-3xl space-y-5">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Terminal className="h-5 w-5 text-brand-700" />
                <span>Smart Grid Solutions — Server SSH Access</span>
              </CardTitle>
              <span className="rounded bg-slate-100 px-2.5 py-1 font-mono text-xs text-slate-800">
                user: {data.linuxUsername}
              </span>
            </div>
          </CardHeader>
          <CardBody className="space-y-4 text-sm text-slate-700">
            <div>
              Welcome <span className="font-semibold text-slate-900">{data.displayName}</span> (
              <code className="font-mono text-xs">{data.email}</code>). Submit your OpenSSH public key below so the target
              servers install your key automatically.
            </div>

            {data.hasPublicKey && data.sshKeyFingerprint ? (
              <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-950">
                <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-700" />
                <span>
                  Active public key registered:{" "}
                  <code className="font-mono font-semibold">{data.sshKeyFingerprint}</code> (propagates to granted servers
                  within 60 seconds). You can replace it below at any time while this link is active.
                </span>
              </div>
            ) : null}

            <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/70 p-4">
              <Label htmlFor="invite-ssh-pubkey" className="flex items-center gap-1.5 text-xs font-semibold text-slate-800">
                <KeyRound className="h-3.5 w-3.5" />
                <span>{data.hasPublicKey ? "Replace SSH public key (.pub)" : "Step 1 — Paste your SSH public key (.pub)"}</span>
              </Label>
              <div className="text-xs text-slate-600">
                Generate an Ed25519 key if needed:{" "}
                <code className="rounded bg-white px-1.5 py-0.5 font-mono">
                  ssh-keygen -t ed25519 -C &quot;{data.email}&quot;
                </code>{" "}
                and paste the single line from <code className="font-mono">~/.ssh/id_ed25519.pub</code>. Never paste your
                private key.
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  id="invite-ssh-pubkey"
                  className="flex-1 font-mono text-xs"
                  placeholder={`ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAI... ${data.email}`}
                  value={publicKeyInput}
                  onChange={(event) => setPublicKeyInput(event.target.value)}
                />
                <Button
                  variant="primary"
                  disabled={submitKeyMutation.isPending || !publicKeyInput.trim()}
                  onClick={() => submitKeyMutation.mutate()}
                >
                  {data.hasPublicKey ? "Update SSH key" : "Register SSH key"}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100 py-3">
            <CardTitle className="text-sm">Step 2 — Connection instructions & allowed commands</CardTitle>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(data.instructions).then(() => {
                  setCopied(true);
                  showToast({ message: "Connection instructions copied.", tone: "success" });
                  setTimeout(() => setCopied(false), 2000);
                });
              }}
            >
              {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
              Copy guide
            </Button>
          </CardHeader>
          <CardBody>
            <pre className="overflow-x-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-950 p-4 font-mono text-xs text-slate-100">
              {data.instructions}
            </pre>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
